import React, { Fragment } from "react";
import PropTypes from "prop-types";

import List from "../custom-widgets/list";
import Button from "../custom-widgets/button";

// Styles
import "./hero-chevron-body.bootstrap.scss";

// Allow HTML in text for typographical HTML entities (ampersand, em-dash, double quotes, etc.) in components
const Heading = (heading) => (
  <div className={`${heading.class} hero-heading`} dangerouslySetInnerHTML={{ __html: heading.text }}></div>
);
const Subheading = (subheading) => (
  <div className={`${subheading.class} hero-subheading`} dangerouslySetInnerHTML={{ __html: subheading.text }}></div>
);
const Paragraph = (paragraph) => (
  <div className={`${paragraph.class} hero-paragraph`} dangerouslySetInnerHTML={{ __html: paragraph.text }}></div>
);
const TertiaryText = (tertiaryText) => (
  <div
    className={`${tertiaryText.class} hero-tertiary-text`}
    dangerouslySetInnerHTML={{ __html: tertiaryText.text }}
  ></div>
);


/**
 * @typedef {Object} ColumnComponents
 * @property {string} id - id of the sub component to render.
 * @property {string} class - classes for the div that renders the text.
 * @property {string} text - required text to show in the sub component.
 * @property {"heading" | "subheading" | "paragraph" | "tertiaryText" | "list" | "button"} type - if none defined we will render the component like this `[<component>]`.
 */

/**
 * @typedef {Object} ColumnData
 * @property {string} id - required id for the div container.
 * @property {string} class - classes applied to the div container.
 * @property {number} contentPlaceholder - this will be true for 3rd Party injection of content. If true we render a `<div id="hero-content-placeholder"></div>` above the code of the components.
 * @property {ColumnComponents[]} components - list of components to render.
 */

/**
 * @typedef {Object} SubComponentData
 * @property {string} id - id of the sub component to render.
 * @property {string} class - classes for the div that renders the text.
 * @property {string} text - required text to show in the sub component.
 */

/**
 * @note - this ComponentData object must have the key "heading" | "subheading" | "paragraph" | "tertiaryText" | "list" | "button", and inside that key we should use these props
 * @typedef {Object} ComponentData
 * @property {SubComponentData} heading - optional renders the `<Heading>` component.
 * @property {SubComponentData} subheading - optional renders the `<Subheading>` component.
 * @property {SubComponentData} paragraph - optional renders the `<Paragraph>` component.
 * @property {SubComponentData} tertiaryText - optional renders the `<TertiaryText>` component.
 * @property {SubComponentData} list - optional renders the `<List>` component.
 * @property {SubComponentData} button - optional renders the `<Button>` component.
 */

/**
 * This is the component to render the Hero Chevron Body.
 *
 * @note - components[] and contentPlaceholder may be at the props root level or inside each column, but not both.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.colClass - Please try NOT to override this. Work with designers to get new images if the text is overlapping with objects on the image.
 * @param {string} props.rowClass - Please try NOT to override this. Work with designers to get new images if the text is overlapping with objects on the image.
 * @param {ColumnData[]} props.columns - array of columns with the component to render in the hero body.
 * @param {boolean} props.contentPlaceholder - this will be true for 3rd Party injection of content. If true we render a `<div id="hero-content-placeholder"></div>` above the code of the components.
 * @param {ComponentData[]} props.components - array of columns with the component to render in the hero body.
 * @returns {React.JSX.Element} Returns the JSX component to render
 */
const HeroChevronBody = ({
  colClass= "col-sm-12 col-md-5",
  rowClass= "",
  columns= null,
  contentPlaceholder= false,
  components= null
}) => {
  // console.log("HeroChevronBody props: ", props);
  const getComponent = (component) => {
    if (component.type === "heading") {
      return <Heading {...component} />;
    } else if (component.type === "subheading") {
      return <Subheading {...component} />;
    } else if (component.type === "paragraph") {
      return <Paragraph {...component} />;
    } else if (component.type === "tertiaryText") {
      return <TertiaryText {...component} />;
    } else if (component.type === "button") {
      return <Button {...component} />;
    } else {
      return component;
    }
  };

  return (
    <div className="hero-chevron-body">
      <div className="container">
        <div className={`row ${rowClass}`}>
          {/* COLUMNS */}
          {columns &&
            columns.map((column, index1) => {
              return (
                <div key={column.id || `hero-column-${index1}`} className={`${column.class}`}>
                  {column.contentPlaceholder && <div id={`hero-content-placeholder-${index1}`}></div>}
                  {column.components &&
                    column.components.map((component, index2) => {
                      return <Fragment key={component.id || `component-${index2}`}>{getComponent(component)}</Fragment>;
                    })}
                </div>
              );
            })}
          {/* NO COLUMNS */}
          {!columns && (
            <div className={colClass}>
              {contentPlaceholder && <div id="hero-content-placeholder"></div>}
              {components &&
                components.map((component, index3) => {
                  return (
                    <Fragment key={component.id || `component-${index3}`}>
                      {component.heading && <Heading {...component.heading} />}
                      {component.subheading && <Subheading {...component.subheading} />}
                      {component.paragraph && <Paragraph {...component.paragraph} />}
                      {component.tertiaryText && <TertiaryText {...component.tertiaryText} />}
                      {component.list && <List {...component.list} />}
                      {component.button && <Button {...component.button} />}
                    </Fragment>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default HeroChevronBody;

HeroChevronBody.propTypes = {
  colClass: PropTypes.string,
  rowClass: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      class: PropTypes.string,
      contentPlaceholder: PropTypes.bool,
      components: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired,
          type: PropTypes.oneOf(["heading", "subheading", "paragraph", "tertiaryText", "list", "button"])
        })
      )
    })
  ),
  contentPlaceholder: PropTypes.bool,
  components: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.shape({
        id: PropTypes.string,
        class: PropTypes.string,
        text: PropTypes.string.isRequired
      }),
      subheading: PropTypes.shape({
        id: PropTypes.string,
        class: PropTypes.string,
        text: PropTypes.string.isRequired
      }),
      paragraph: PropTypes.shape({
        id: PropTypes.string,
        class: PropTypes.string,
        text: PropTypes.string.isRequired
      }),
      tertiaryText: PropTypes.shape({
        id: PropTypes.string,
        class: PropTypes.string,
        text: PropTypes.string.isRequired
      }),
      list: PropTypes.shape({
        id: PropTypes.string,
        class: PropTypes.string,
        text: PropTypes.string.isRequired
      }),
      button: PropTypes.shape({
        id: PropTypes.string,
        class: PropTypes.string,
        text: PropTypes.string.isRequired
      })
    })
  )
};
